import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { GetCoverage } from "./GetCoverage";
import { Coverage } from "fhir/r5";

// Tipos para os parâmetros e retorno do hook
interface FetchCoverageParams {
  insurer?: string;
  patient?: string;
  identifier?: string;
  count?: number;
}

interface Bundle {
  entry?: Array<{ resource: Coverage }>;
}

interface UseCoverageReturn {
  coverage: Coverage[];
  loading: boolean;
  fetchCoverage: (params: FetchCoverageParams) => Promise<{
    coverages: Coverage[];
  }>;
  clearCoverage: () => void;
}

// Hook para buscar e gerenciar coberturas de planos de saúde
export const useCoverage = (): UseCoverageReturn => {
  const [coverage, setCoverage] = useState<Coverage[]>([]);
  const [loading, setLoading] = useState(false);

  // Função para buscar coberturas
  const fetchCoverage = useCallback(
    async ({ insurer, patient, identifier, count }: FetchCoverageParams) => {
      setLoading(true);
      try {
        // Buscar coberturas com os parâmetros fornecidos
        const response = (await GetCoverage({
          insurer,
          patient,
          identifier,
          count,
        })) as Bundle;

        const coverages: Coverage[] = [];

        if (response?.entry?.length) {
          // Processar os recursos de cobertura
          response.entry.forEach((entry) => {
            const resource = entry.resource;
            if (resource?.resourceType === "Coverage") {
              coverages.push(resource as Coverage);
            }
          });
        }

        setCoverage(coverages);

        return {
          coverages,
        };
      } catch (error) {
        toast.error("Erro ao carregar coberturas");
        setCoverage([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Função para limpar o estado das coberturas
  const clearCoverage = useCallback(() => {
    setCoverage([]);
  }, []);

  return {
    coverage,
    loading,
    fetchCoverage,
    clearCoverage,
  };
};
